import React from "react";
import { Minus, Plus } from "lucide-react";
import telegramService from "../../utils/telegram";

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  /** Masalan "kg", "sm", "kkal" — inputning o'ng chetida ko'rinadi */
  suffix?: string;
}

export const Input: React.FC<InputProps> = ({
  label,
  error,
  suffix,
  className = "",
  id,
  ...rest
}) => {
  const inputId = id || (label ? `input-${label.replace(/\s+/g, "-").toLowerCase()}` : undefined);

  return (
    <div className="w-full">
      {label && (
        <label
          htmlFor={inputId}
          className="block text-xs font-bold text-content-secondary mb-1.5"
        >
          {label}
        </label>
      )}
      <div className="relative">
        <input
          id={inputId}
          className={`form-input ${error ? "border-danger focus:border-danger focus:ring-danger/25" : ""} ${suffix ? "pr-12" : ""} ${className}`}
          {...rest}
        />
        {suffix && (
          <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs font-bold text-content-tertiary pointer-events-none">
            {suffix}
          </span>
        )}
      </div>
      {error && <p className="mt-1.5 text-xs font-medium text-danger">{error}</p>}
    </div>
  );
};

interface StepperProps {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  label?: string;
  suffix?: string;
}

export const Stepper: React.FC<StepperProps> = ({
  value,
  onChange,
  min = 0,
  max = Number.MAX_SAFE_INTEGER,
  step = 1,
  label,
  suffix,
}) => {
  const update = (next: number) => {
    const clamped = Math.min(max, Math.max(min, next));
    if (clamped !== value) {
      telegramService.haptic("selection");
      onChange(clamped);
    }
  };

  return (
    <div className="w-full">
      {label && (
        <span className="block text-xs font-bold text-content-secondary mb-1.5">{label}</span>
      )}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => update(value - step)}
          disabled={value <= min}
          aria-label="-"
          className="w-11 h-11 rounded-2xl bg-inset text-content-primary flex items-center justify-center active:scale-95 transition-transform disabled:opacity-40"
        >
          <Minus size={18} />
        </button>
        <div className="flex-1 h-11 rounded-2xl border border-line-strong bg-surface flex items-center justify-center gap-1">
          <span className="text-base font-black text-content-primary tabular-nums">{value}</span>
          {suffix && <span className="text-xs font-bold text-content-tertiary">{suffix}</span>}
        </div>
        <button
          type="button"
          onClick={() => update(value + step)}
          disabled={value >= max}
          aria-label="+"
          className="w-11 h-11 rounded-2xl bg-inset text-content-primary flex items-center justify-center active:scale-95 transition-transform disabled:opacity-40"
        >
          <Plus size={18} />
        </button>
      </div>
    </div>
  );
};

export default Input;
